import type { WorkforceAccount } from "@emr/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createWorkforceAccountClient } from "../api/workforceAccount";
import { useClinicalRuntime } from "../auth/ClinicalRuntimeProvider";

export const workforceAccountKey = (siteId: string, userId = "me") =>
  ["workforce-account", siteId, userId] as const;
export function useWorkforceAccount(userId = "me") {
  const runtime = useClinicalRuntime();
  const { agentApiOrigin, siteId } = runtime.config;
  const getAccessToken = runtime.workforce.accessToken;
  const client = useMemo(
    () => createWorkforceAccountClient({ baseUrl: agentApiOrigin, siteId, getAccessToken }),
    [agentApiOrigin, siteId, getAccessToken],
  );
  const query = useQuery({
    queryKey: workforceAccountKey(siteId, userId),
    queryFn: ({ signal }) => client.get(userId, signal),
  });
  return { ...query, client };
}
/**
 * The account's physician has a patient handout drafted for review at every visit they compose
 * (EXECDEV-251). Off for every other role, and while the account is loading or failed to load.
 */
export function draftsHandoutEveryVisit(account: WorkforceAccount | null | undefined): boolean {
  return account?.role === "physician" && account.patientHandoutEveryVisit === true;
}
export function workforceInitials(name: string): string {
  return (
    name
      .replace(/^(dr\.?|mr\.?|mrs\.?|ms\.?)\s+/i, "")
      .split(/\s+/u)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => [...word][0])
      .join("")
      .toUpperCase() || "?"
  );
}
export function workforceRoleLabel(role: string): string {
  return role === "nurse_ma"
    ? "Nurse / medical assistant"
    : role.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
}

export async function prepareProfilePhoto(file: File): Promise<string> {
  if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 5_000_000 || file.size === 0)
    throw new Error("Choose a PNG or JPEG photo up to 5 MB.");
  const image = await createImageBitmap(file);
  try {
    if (!image.width || !image.height || image.width * image.height > 32_000_000)
      throw new Error("Choose a smaller photo.");
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Photo processing is unavailable in this browser.");
    const edge = Math.min(image.width, image.height);
    context.drawImage(
      image,
      (image.width - edge) / 2,
      (image.height - edge) / 2,
      edge,
      edge,
      0,
      0,
      128,
      128,
    );
    const result = canvas.toDataURL("image/png");
    if (result.length > 100_000) throw new Error("Choose a smaller photo.");
    return result;
  } finally {
    image.close();
  }
}
